import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Image as ImageIcon, Move, Sparkles, Wand2, Pencil, Crop, ArrowUp, ArrowDown,
  ArrowLeft, ArrowRight, Maximize2, RotateCw, Loader2, Upload, FolderOpen, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Squarespace-style Image Editor right-side panel.
 * Opens on double-click of any image in the canvas.
 * Tabs: Content / Design / Animation
 */
export default function ImageEditorPanel({
  block,
  field = 'image_url', // or 'image'
  imageUrl,
  onUpdate, // (block.id, field, value)
  onClose,
  onMediaPick,
}) {
  const [tab, setTab] = useState('content');
  const [uploading, setUploading] = useState(false);
  const c = block?.content || {};
  const s = c._style || {};
  const altText = c.alt_text || c.caption || '';
  const focusPoint = c.focus_point || { x: 50, y: 50 };
  const animation = s.animation || 'none';
  const shape = c.shape || s.shape || 'rectangle';
  const filter = s.filter || 'none';
  const radius = s.radius ?? 12;
  const token = localStorage.getItem('token');

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onUpdate(block.id, field, res.data.url);
      toast.success('Image uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updateStyle = (key, val) => {
    onUpdate(block.id, '_style', { ...s, [key]: val });
  };

  const updateContent = async (key, val) => {
    onUpdate(block.id, key, val);
  };

  return (
    <div className="fixed top-[64px] right-6 z-[170] w-[360px] max-h-[calc(100vh-100px)] bg-[#15152E] border border-white/[0.08] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col" data-testid="image-editor-panel">
      {/* HEADER */}
      <div className="px-5 pt-4 pb-3 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">Editing Image</p>
            <p className="text-white font-bold text-[13px] mt-0.5">Image Block</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-white p-1" data-testid="img-editor-close"><X size={14} /></button>
        </div>
        {/* TABS */}
        <div className="flex items-center gap-5 mt-4 -mb-px">
          {['content', 'design', 'animation'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-[12px] font-semibold pb-2 border-b-2 transition-all capitalize ${
                tab === t ? 'text-white border-[#A78BFA]' : 'text-[#9CA3AF] border-transparent hover:text-white'
              }`}
              data-testid={`img-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* PREVIEW */}
      <div className="px-5 py-4 border-b border-white/[0.06]">
        <div className={`relative w-full bg-[#0E0E23] overflow-hidden ${shape === 'circle' ? 'rounded-full aspect-square' : 'rounded-lg aspect-[16/10]'}`} style={{ borderRadius: shape === 'circle' ? '50%' : radius }}>
          {imageUrl ? (
            <img
              src={imageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` : imageUrl}
              alt={altText}
              className="w-full h-full object-cover"
              style={{
                objectPosition: `${focusPoint.x}% ${focusPoint.y}%`,
                filter: filter === 'none' ? '' : `${filter}`,
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#64748B]">
              <ImageIcon size={28} />
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-3">
          <label className="flex-1 cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="img-upload-replace" />
            <span className="w-full block px-3 py-2 text-[11px] text-center font-bold uppercase tracking-wider text-white bg-white/[0.05] hover:bg-white/[0.1] rounded-lg transition-all">
              {uploading ? <Loader2 size={12} className="inline animate-spin" /> : <><Upload size={11} className="inline mr-1" /> Replace</>}
            </span>
          </label>
          <button onClick={onMediaPick} className="flex-1 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white bg-white/[0.05] hover:bg-white/[0.1] rounded-lg transition-all" data-testid="img-library">
            <FolderOpen size={11} className="inline mr-1" /> Library
          </button>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto px-5 py-4 sqs-scrollbar text-white">
        {tab === 'content' && (
          <div className="space-y-4">
            <Field label="Alt text" hint="For screen readers & SEO">
              <input
                value={altText}
                onChange={(e) => updateContent('alt_text', e.target.value)}
                placeholder="Describe what the image shows"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                data-testid="img-alt"
              />
            </Field>
            <Field label="Caption" hint="Optional caption shown below the image">
              <input
                value={c.caption || ''}
                onChange={(e) => updateContent('caption', e.target.value)}
                placeholder="Optional caption"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                data-testid="img-caption"
              />
            </Field>
            <Field label="Link to" hint="Where the image points when clicked">
              <input
                type="url"
                value={c.linked_url || ''}
                onChange={(e) => updateContent('linked_url', e.target.value)}
                placeholder="https://"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                data-testid="img-link"
              />
            </Field>

            <Field label="Focal point" hint="Drag the dot to set the focus of the crop">
              <FocalPicker imageUrl={imageUrl} focusPoint={focusPoint} onChange={(fp) => updateContent('focus_point', fp)} />
            </Field>
          </div>
        )}

        {tab === 'design' && (
          <div className="space-y-4">
            <Field label="Shape">
              <div className="grid grid-cols-3 gap-2">
                {['rectangle', 'rounded', 'circle'].map(sh => (
                  <button
                    key={sh}
                    onClick={() => updateContent('shape', sh)}
                    className={`py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-all ${shape === sh ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'}`}
                    data-testid={`img-shape-${sh}`}
                  >
                    {sh}
                  </button>
                ))}
              </div>
            </Field>
            {shape !== 'circle' && (
              <Field label="Corner radius">
                <input
                  type="range"
                  min={0}
                  max={48}
                  value={radius}
                  onChange={(e) => updateStyle('radius', parseInt(e.target.value))}
                  className="w-full accent-[#A78BFA]"
                  data-testid="img-radius"
                />
                <p className="text-[10px] text-[#64748B] mt-1">{radius}px</p>
              </Field>
            )}
            <Field label="Filter">
              <select
                value={filter}
                onChange={(e) => updateStyle('filter', e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white focus:outline-none focus:border-[#8B5CF6]/40"
                data-testid="img-filter"
              >
                <option value="none">None</option>
                <option value="grayscale(100%)">Grayscale</option>
                <option value="sepia(80%)">Sepia</option>
                <option value="brightness(1.1) contrast(1.05)">Vivid</option>
                <option value="brightness(0.9) contrast(1.1)">Moody</option>
                <option value="saturate(0.7)">Faded</option>
                <option value="contrast(1.2) brightness(0.95)">Cinematic</option>
              </select>
            </Field>
            <Field label="Alignment">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: 'left', icon: ArrowLeft },
                  { v: 'center', icon: Move },
                  { v: 'right', icon: ArrowRight },
                ].map(({ v, icon: Ic }) => (
                  <button
                    key={v}
                    onClick={() => updateContent('alignment', v)}
                    className={`py-2 rounded-md flex items-center justify-center transition-all ${c.alignment === v ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'}`}
                    data-testid={`img-align-${v}`}
                  >
                    <Ic size={14} />
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Size">
              <div className="grid grid-cols-4 gap-2">
                {['small', 'medium', 'large', 'full'].map(sz => (
                  <button
                    key={sz}
                    onClick={() => updateStyle('imageSize', sz)}
                    className={`py-2 rounded-md text-[10.5px] font-semibold uppercase tracking-wider transition-all ${(s.imageSize || 'medium') === sz ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'}`}
                    data-testid={`img-size-${sz}`}
                  >
                    {sz}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}

        {tab === 'animation' && (
          <div className="space-y-4">
            <Field label="Entrance animation" hint="Plays when the image scrolls into view">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'none', label: 'None' },
                  { v: 'fade', label: 'Fade in' },
                  { v: 'fade-up', label: 'Fade up' },
                  { v: 'fade-down', label: 'Fade down' },
                  { v: 'zoom', label: 'Zoom in' },
                  { v: 'slide-left', label: 'Slide left' },
                  { v: 'slide-right', label: 'Slide right' },
                  { v: 'blur', label: 'Blur in' },
                ].map(o => (
                  <button
                    key={o.v}
                    onClick={() => updateStyle('animation', o.v)}
                    className={`py-2.5 rounded-md text-[11.5px] font-semibold transition-all ${animation === o.v ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'}`}
                    data-testid={`img-anim-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Hover effect">
              <div className="grid grid-cols-3 gap-2">
                {['none', 'zoom', 'lift'].map(h => (
                  <button
                    key={h}
                    onClick={() => updateStyle('hover', h)}
                    className={`py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-all ${(s.hover || 'none') === h ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/[0.04] text-[#9CA3AF] hover:bg-white/[0.08]'}`}
                    data-testid={`img-hover-${h}`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
        <button
          onClick={async () => { if ((await themedConfirm('Remove this image?'))) { updateContent(field, ''); onClose(); } }}
          className="text-[10px] uppercase tracking-[0.18em] text-[#FCA5A5] font-bold hover:text-[#FF6B6B] flex items-center gap-1"
          data-testid="img-remove"
        >
          <Trash2 size={11} /> Remove
        </button>
        <button onClick={onClose} className="text-[10px] uppercase tracking-[0.18em] text-white font-bold hover:text-[#A78BFA]">Done</button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-1.5">{label}</p>
      {hint && <p className="text-[10.5px] text-[#9CA3AF] mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function FocalPicker({ imageUrl, focusPoint, onChange }) {
  const [dragging, setDragging] = useState(false);
  const handleMove = (e) => {
    if (!dragging) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
    onChange({ x: Math.round(x), y: Math.round(y) });
  };
  return (
    <div
      className="relative w-full aspect-[16/10] bg-[#0E0E23] rounded-lg overflow-hidden cursor-crosshair select-none"
      onMouseDown={() => setDragging(true)}
      onMouseUp={() => setDragging(false)}
      onMouseLeave={() => setDragging(false)}
      onMouseMove={handleMove}
      onClick={(e) => { setDragging(true); handleMove(e); setDragging(false); }}
      data-testid="focal-picker"
    >
      {imageUrl ? (
        <img
          src={imageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` : imageUrl}
          alt=""
          className="w-full h-full object-cover pointer-events-none"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#64748B] text-[11px]">No image</div>
      )}
      <div
        className="absolute w-5 h-5 rounded-full bg-white border-2 border-[#A78BFA] shadow-[0_0_12px_rgba(167,139,250,0.6)] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ left: `${focusPoint.x}%`, top: `${focusPoint.y}%` }}
      />
    </div>
  );
}
